<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { Line, Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

import { getContratoFinanciero } from 'dashboard/api/supra';

const props = defineProps({
  numeroContrato: {
    type: String,
    required: true,
  },
  explotacion: {
    type: String,
    required: true,
  },
  contrato: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const deudaData = ref(null);
const facturas = ref([]);
const facturasError = ref(null);
const isLoading = ref(true);
const error = ref(null);

const datosPago = computed(
  () => props.contrato?.rawData?.GenericoContratoDTO?.datosPago || {}
);
const tienePlanPago = computed(
  () =>
    props.contrato?.rawData?.GenericoContratoDTO?.contrato?.tienePlanPago === 'S'
);

const deudaTotal = computed(() =>
  parseFloat(deudaData.value?.deudaTotal || '0')
);
const deudaPrincipal = computed(() =>
  parseFloat(deudaData.value?.deuda || '0')
);
const deudaComision = computed(() =>
  parseFloat(deudaData.value?.deudaComision || '0')
);
const saldoTotal = computed(() =>
  parseFloat(deudaData.value?.saldoAnteriorTotal || '0')
);
const ciclosTotalesNum = computed(() => {
  const v = parseFloat(String(deudaData.value?.ciclosTotales ?? '0'), 10);
  return Number.isNaN(v) ? 0 : v;
});
const tieneDeuda = computed(() => deudaTotal.value > 0.01);

const formatCurrency = value => `$${value.toFixed(2)}`;

function invoiceIsPagada(estado) {
  if (estado === '3' || estado === 3) return true;
  const u = String(estado || '').toUpperCase();
  return u === 'COBRADA' || u === 'PAGADA';
}

function invoiceIsPendiente(estado) {
  if (estado === '4' || estado === 4) return true;
  return String(estado || '').toUpperCase() === 'PENDIENTE';
}

function invoiceIsVencida(estado) {
  const u = String(estado || '').toUpperCase();
  return u === 'VENCIDA' || u === 'IMPAGADA';
}

const pagadasCount = computed(
  () => facturas.value.filter(f => invoiceIsPagada(f.estado)).length
);
const pendientesCount = computed(
  () => facturas.value.filter(f => invoiceIsPendiente(f.estado)).length
);
const vencidasCount = computed(
  () => facturas.value.filter(f => invoiceIsVencida(f.estado)).length
);
const otrosCount = computed(
  () =>
    facturas.value.length -
    pagadasCount.value -
    pendientesCount.value -
    vencidasCount.value
);

const ratioPuntualidad = computed(() => {
  const p = pagadasCount.value;
  const v = vencidasCount.value;
  if (p + v === 0) return null;
  return (p / (p + v)) * 100;
});

const promedioFacturado = computed(() => {
  if (facturas.value.length === 0) return 0;
  const sum = facturas.value.reduce(
    (acc, f) => acc + parseFloat(f.importeTotal || 0),
    0
  );
  return sum / facturas.value.length;
});

const ultimaFactura = computed(() => {
  if (facturas.value.length === 0) return null;
  const sorted = [...facturas.value].sort((a, b) => {
    const da = new Date(a.fechaEmision).getTime();
    const db = new Date(b.fechaEmision).getTime();
    return db - da;
  });
  return sorted[0];
});

const semaforoKey = computed(() => {
  const dt = deudaTotal.value;
  const c = ciclosTotalesNum.value;
  if (dt <= 0.01) return 'ok';
  if (c <= 1) return 'warn';
  return 'bad';
});

const antiguedadTexto = computed(() => {
  const raw =
    props.contrato?.rawData?.GenericoContratoDTO?.contrato?.fechaAlta ||
    props.contrato?.fechaAlta;
  if (!raw) return t('CONTRACTS.FINANCIERO.NOT_AVAILABLE');
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return typeof raw === 'string' ? raw : t('CONTRACTS.FINANCIERO.NOT_AVAILABLE');
  }
  const now = new Date();
  let months =
    (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (now.getDate() < d.getDate()) months -= 1;
  const years = Math.floor(months / 12);
  const mo = months % 12;
  if (years > 0) {
    return t('CONTRACTS.FINANCIERO.TENURE_YM', { years, months: mo });
  }
  return t('CONTRACTS.FINANCIERO.TENURE_M', { months: Math.max(0, mo) });
});

const riskLevel = computed(() => {
  const dt = deudaTotal.value;
  const ciclos = ciclosTotalesNum.value;
  const ratio = ratioPuntualidad.value;
  if (facturas.value.length < 3) return 'MEDIO';
  if (ciclos > 2) return 'ALTO';
  if (ratio !== null && ratio < 50) return 'ALTO';
  if (dt <= 0.01 && ratio !== null && ratio >= 80) return 'BAJO';
  if (dt > 0.01 && ciclos <= 2 && ratio !== null && ratio >= 50) return 'MEDIO';
  return 'MEDIO';
});

const chartLineData = computed(() => {
  const rows = facturas.value
    .map(f => ({
      fecha: f.fechaEmision,
      importe: parseFloat(f.importeTotal || 0),
      estado: f.estado,
    }))
    .filter(r => r.fecha && !Number.isNaN(new Date(r.fecha).getTime()))
    .sort(
      (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime()
    );

  const pointColor = estado => {
    if (invoiceIsPagada(estado)) return 'rgb(34, 197, 94)';
    if (invoiceIsVencida(estado)) return 'rgb(244, 63, 94)';
    if (invoiceIsPendiente(estado)) return 'rgb(245, 158, 11)';
    return 'rgb(100, 116, 139)';
  };

  return {
    labels: rows.map(r =>
      new Date(r.fecha).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
      })
    ),
    datasets: [
      {
        label: t('CONTRACTS.FINANCIERO.CHART_INVOICE_AMOUNT'),
        data: rows.map(r => r.importe),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.35,
        pointRadius: 5,
        pointBackgroundColor: rows.map(r => pointColor(r.estado)),
      },
    ],
  };
});

const chartLineOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: true, position: 'bottom' },
    tooltip: {
      callbacks: {
        label: ctx => {
          const y = ctx.parsed?.y;
          return `${ctx.dataset.label}: ${formatCurrency(y ?? 0)}`;
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: v => formatCurrency(v),
      },
    },
  },
}));

const chartDonutData = computed(() => {
  const p = pagadasCount.value;
  const pe = pendientesCount.value;
  const v = vencidasCount.value;
  const o = Math.max(0, otrosCount.value);
  const sum = p + pe + v + o;
  if (sum === 0) {
    return {
      labels: [t('CONTRACTS.FINANCIERO.NO_DATA')],
      datasets: [
        {
          data: [1],
          backgroundColor: ['rgba(148, 163, 184, 0.5)'],
        },
      ],
    };
  }
  return {
    labels: [
      t('CONTRACTS.FACTURAS.PAID'),
      t('CONTRACTS.FACTURAS.PENDING'),
      t('CONTRACTS.FINANCIERO.OVERDUE_LABEL'),
      t('CONTRACTS.FINANCIERO.OTHER_STATUS'),
    ],
    datasets: [
      {
        data: [p, pe, v, o],
        backgroundColor: [
          'rgba(34, 197, 94, 0.85)',
          'rgba(245, 158, 11, 0.85)',
          'rgba(244, 63, 94, 0.85)',
          'rgba(100, 116, 139, 0.65)',
        ],
      },
    ],
  };
});

const chartDonutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom' },
  },
};

function normalizeFacturasResponse(data) {
  if (data?.Factura) {
    return Array.isArray(data.Factura) ? data.Factura : [data.Factura];
  }
  if (data?.factura) {
    return Array.isArray(data.factura) ? data.factura : [data.factura];
  }
  if (Array.isArray(data)) return data;
  return [];
}

async function loadDeuda(_explotacionFormatted) {
  const res = await getContratoFinanciero(props.numeroContrato, props.explotacion);
  const { resumen } = res.data?.data ?? {};
  if (!resumen) throw new Error('Respuesta vacía del financiero');

  deudaData.value = {
    deudaTotal: resumen.saldoTotal?.toFixed(2) ?? '0',
    deuda: resumen.deudaVencida?.toFixed(2) ?? '0',
    deudaComision: resumen.recargosAcumulados?.toFixed(2) ?? '0',
    saldoAnteriorTotal: '0',
    ciclosTotales: undefined,
    nombreCliente: undefined,
  };
}

async function loadFacturas(_explotacionFormatted) {
  const res = await getContratoFinanciero(props.numeroContrato, props.explotacion);
  const movimientos = res.data?.data?.movimientos ?? [];
  facturas.value = movimientos.map(m => ({
    numero: m.referencia || '',
    numeroFactura: m.referencia || '',
    fechaEmision: m.fecha,
    importeTotal: Math.abs(m.importe),
    estado: m.tipo === 'pago' ? 'PAGADA' : m.tipo.toUpperCase(),
  }));
}

async function loadAll() {
  isLoading.value = true;
  error.value = null;
  facturasError.value = null;

  try {
    await loadDeuda();
  } catch (err) {
    error.value = `Error al cargar deuda: ${err.message || 'Error desconocido'}`;
    useAlert(error.value);
    isLoading.value = false;
    return;
  }

  try {
    await loadFacturas();
  } catch (err) {
    facturasError.value =
      err.message || t('CONTRACTS.FINANCIERO.FACTURAS_PARTIAL_ERROR');
    facturas.value = [];
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  loadAll();
});
</script>

<template>
  <div class="space-y-6">
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <p class="text-sm text-n-slate-11 mt-2">
          {{ $t('CONTRACTS.DEUDA.LOADING') }}
        </p>
      </div>
    </div>

    <div
      v-else-if="error || !deudaData"
      class="flex flex-col items-center justify-center py-12 gap-4"
    >
      <span class="i-lucide-alert-circle text-6xl text-n-ruby-11" />
      <p class="text-n-slate-11">
        {{ error || $t('CONTRACTS.DEUDA.NOT_FOUND') }}
      </p>
    </div>

    <template v-else>
      <!-- S1: Resumen -->
      <div
        class="rounded-lg border p-4"
        :class="{
          'bg-green-50 border-green-200 text-green-900': semaforoKey === 'ok',
          'bg-amber-50 border-amber-200 text-amber-950':
            semaforoKey === 'warn',
          'bg-n-ruby-3 border-n-ruby-6 text-n-ruby-11': semaforoKey === 'bad',
        }"
      >
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p class="text-xs font-medium uppercase tracking-wide opacity-80">
              {{ $t('CONTRACTS.FINANCIERO.STATUS_TITLE') }}
            </p>
            <p class="text-lg font-semibold mt-1">
              <span v-if="semaforoKey === 'ok'">{{
                $t('CONTRACTS.FINANCIERO.STATUS_OK')
              }}</span>
              <span v-else-if="semaforoKey === 'warn'">{{
                $t('CONTRACTS.FINANCIERO.STATUS_WARN')
              }}</span>
              <span v-else>{{ $t('CONTRACTS.FINANCIERO.STATUS_BAD') }}</span>
            </p>
            <p class="text-sm mt-2">
              {{ $t('CONTRACTS.COMMON.TOTAL_DEBT_LABEL') }}:
              <strong>{{ formatCurrency(deudaTotal) }}</strong>
              · {{ $t('CONTRACTS.COMMON.TOTAL_CYCLES') }}:
              <strong>{{ deudaData.ciclosTotales ?? '—' }}</strong>
            </p>
          </div>
          <div class="grid gap-2 text-sm min-w-[200px]">
            <div class="flex justify-between gap-4">
              <span class="text-n-slate-11">{{
                $t('CONTRACTS.COMMON.CLIENT_NAME')
              }}</span>
              <span class="font-medium text-right">{{
                deudaData.nombreCliente ||
                contrato?.titular ||
                $t('CONTRACTS.FINANCIERO.NOT_AVAILABLE')
              }}</span>
            </div>
            <div class="flex justify-between gap-4">
              <span class="text-n-slate-11">{{
                $t('CONTRACTS.FINANCIERO.PAYMENT_METHOD')
              }}</span>
              <span class="font-medium text-right">{{
                datosPago.formaPago ||
                $t('CONTRACTS.FINANCIERO.NOT_AVAILABLE')
              }}</span>
            </div>
            <div class="flex justify-between gap-4 items-center">
              <span class="text-n-slate-11">{{
                $t('CONTRACTS.FINANCIERO.PAYMENT_PLAN')
              }}</span>
              <Badge :color="tienePlanPago ? 'blue' : 'slate'" size="small">
                {{
                  tienePlanPago
                    ? $t('CONTRACTS.FINANCIERO.PLAN_YES')
                    : $t('CONTRACTS.FINANCIERO.PLAN_NO')
                }}
              </Badge>
            </div>
            <div class="flex justify-between gap-4">
              <span class="text-n-slate-11">{{
                $t('CONTRACTS.FINANCIERO.TENURE')
              }}</span>
              <span class="font-medium text-right">{{ antiguedadTexto }}</span>
            </div>
            <div class="flex justify-between gap-4">
              <span class="text-n-slate-11">{{
                $t('CONTRACTS.DETAIL.USE_TYPE')
              }}</span>
              <span class="font-medium text-right">{{
                contrato?.descUso || '—'
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- S2: Desglose deuda -->
      <div class="grid gap-4 md:grid-cols-2">
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <h3
            class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-dollar-sign" />
            {{ $t('CONTRACTS.COMMON.CURRENT_DEBT') }}
          </h3>
          <div class="space-y-3">
            <div class="flex justify-between items-center pb-2">
              <span class="text-xs text-n-slate-11">{{
                $t('CONTRACTS.COMMON.MAIN_DEBT')
              }}</span>
              <span
                class="text-sm font-semibold"
                :class="
                  deudaPrincipal > 0 ? 'text-n-ruby-11' : 'text-green-600'
                "
              >
                {{ formatCurrency(deudaPrincipal) }}
              </span>
            </div>
            <div class="border-t border-n-weak" />
            <div class="flex justify-between items-center pb-2">
              <span class="text-xs text-n-slate-11">{{
                $t('CONTRACTS.COMMON.COMMISSIONS')
              }}</span>
              <span
                class="text-sm font-semibold"
                :class="deudaComision > 0 ? 'text-n-ruby-11' : 'text-green-600'"
              >
                {{ formatCurrency(deudaComision) }}
              </span>
            </div>
            <div class="border-t border-n-weak" />
            <div
              class="flex justify-between items-center pt-2 bg-n-alpha-1 p-3 rounded-lg"
            >
              <span class="text-sm font-semibold">{{
                $t('CONTRACTS.COMMON.TOTAL_DEBT_LABEL')
              }}</span>
              <span
                class="text-xl font-bold"
                :class="deudaTotal > 0 ? 'text-n-ruby-11' : 'text-green-600'"
              >
                {{ formatCurrency(deudaTotal) }}
              </span>
            </div>
            <div
              class="grid grid-cols-2 gap-4 text-center pt-2 border-t border-n-weak"
            >
              <div>
                <p class="text-xs text-n-slate-11">
                  {{ $t('CONTRACTS.COMMON.PREVIOUS_CYCLES') }}
                </p>
                <p class="text-lg font-bold text-n-slate-12">
                  {{ deudaData.ciclosAnteriores }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-11">
                  {{ $t('CONTRACTS.COMMON.TOTAL_CYCLES') }}
                </p>
                <p class="text-lg font-bold text-n-slate-12">
                  {{ deudaData.ciclosTotales }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <h3
            class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-calendar" />
            {{ $t('CONTRACTS.COMMON.PREVIOUS_BALANCE') }}
          </h3>
          <div class="space-y-3">
            <div class="flex justify-between items-center pb-2">
              <span class="text-xs text-n-slate-11">{{
                $t('CONTRACTS.COMMON.MAIN_BALANCE')
              }}</span>
              <span class="text-sm font-semibold text-n-slate-12">
                {{ formatCurrency(parseFloat(deudaData.saldoAnterior || '0')) }}
              </span>
            </div>
            <div class="border-t border-n-weak" />
            <div class="flex justify-between items-center pb-2">
              <span class="text-xs text-n-slate-11">{{
                $t('CONTRACTS.COMMON.COMMISSIONS')
              }}</span>
              <span class="text-sm font-semibold text-n-slate-12">
                {{
                  formatCurrency(
                    parseFloat(deudaData.saldoAnteriorComision || '0')
                  )
                }}
              </span>
            </div>
            <div class="border-t border-n-weak" />
            <div
              class="flex justify-between items-center pt-2 bg-n-alpha-1 p-3 rounded-lg"
            >
              <span class="text-sm font-semibold">{{
                $t('CONTRACTS.COMMON.TOTAL_PREVIOUS_BALANCE')
              }}</span>
              <span class="text-xl font-bold text-n-slate-12">
                {{ formatCurrency(saldoTotal) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3
          class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
        >
          <span class="i-lucide-file-text" />
          {{ $t('CONTRACTS.COMMON.PAYMENT_DOCUMENTS') }}
        </h3>
        <div class="grid gap-4 md:grid-cols-2">
          <div>
            <p class="text-xs text-n-slate-11 mb-2">
              {{ $t('CONTRACTS.COMMON.CURRENT_PAYMENT_DOC') }}
            </p>
            <div
              v-if="deudaData.documentoPago"
              class="flex items-center gap-2 p-3 bg-n-alpha-1 rounded-lg"
            >
              <span class="i-lucide-file-text text-n-brand" />
              <span class="text-sm font-medium font-mono text-n-slate-12">{{
                deudaData.documentoPago
              }}</span>
            </div>
            <p
              v-else
              class="text-xs text-n-slate-10 italic p-3 bg-n-solid-3 rounded-lg"
            >
              {{ $t('CONTRACTS.COMMON.NOT_AVAILABLE') }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-11 mb-2">
              {{ $t('CONTRACTS.COMMON.PREVIOUS_PAYMENT_DOC') }}
            </p>
            <div
              v-if="deudaData.documentoPagoAnterior"
              class="flex items-center gap-2 p-3 bg-n-alpha-1 rounded-lg"
            >
              <span class="i-lucide-file-text text-n-slate-11" />
              <span class="text-sm font-medium font-mono text-n-slate-12">{{
                deudaData.documentoPagoAnterior
              }}</span>
            </div>
            <p
              v-else
              class="text-xs text-n-slate-10 italic p-3 bg-n-solid-3 rounded-lg"
            >
              {{ $t('CONTRACTS.COMMON.NOT_AVAILABLE') }}
            </p>
          </div>
        </div>
      </div>

      <!-- S3 + S4: Análisis facturas -->
      <div v-if="facturasError" class="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
        {{ facturasError }}
      </div>

      <div
        v-if="!facturasError && facturas.length > 0"
        class="bg-n-background border border-n-weak rounded-lg p-4 space-y-4"
      >
        <h3
          class="text-lg font-semibold text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-lucide-pie-chart" />
          {{ $t('CONTRACTS.FINANCIERO.INVOICE_ANALYSIS') }}
        </h3>
        <p
          v-if="facturas.length < 3"
          class="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1 inline-block"
        >
          {{ $t('CONTRACTS.FINANCIERO.INSUFFICIENT_INVOICES') }}
        </p>

        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div class="rounded-lg border border-n-weak p-3">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONTRACTS.FINANCIERO.COUNT_INVOICES') }}
            </p>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ facturas.length }}
            </p>
          </div>
          <div class="rounded-lg border border-n-weak p-3">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONTRACTS.FINANCIERO.ON_TIME_RATIO') }}
            </p>
            <p class="text-2xl font-bold text-n-slate-12">
              {{
                ratioPuntualidad !== null
                  ? `${ratioPuntualidad.toFixed(0)}%`
                  : '—'
              }}
            </p>
          </div>
          <div class="rounded-lg border border-n-weak p-3">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONTRACTS.FINANCIERO.AVG_INVOICE') }}
            </p>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ formatCurrency(promedioFacturado) }}
            </p>
          </div>
          <div class="rounded-lg border border-n-weak p-3">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONTRACTS.FINANCIERO.LAST_INVOICE') }}
            </p>
            <p class="text-sm font-semibold text-n-slate-12">
              {{
                ultimaFactura
                  ? `${ultimaFactura.numero || '—'} · ${formatCurrency(parseFloat(ultimaFactura.importeTotal || 0))}`
                  : '—'
              }}
            </p>
          </div>
        </div>

        <div class="grid gap-6 lg:grid-cols-2">
          <div class="h-64">
            <Doughnut :data="chartDonutData" :options="chartDonutOptions" />
          </div>
          <div class="h-64">
            <Line :data="chartLineData" :options="chartLineOptions" />
          </div>
        </div>
      </div>

      <!-- S5: Riesgo -->
      <div class="bg-n-background border border-n-weak rounded-lg p-4">
        <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
          {{ $t('CONTRACTS.FINANCIERO.RISK_TITLE') }}
        </h3>
        <Badge
          :color="
            riskLevel === 'BAJO'
              ? 'green'
              : riskLevel === 'MEDIO'
                ? 'amber'
                : 'ruby'
          "
          size="small"
        >
          {{ $t(`CONTRACTS.FINANCIERO.RISK_${riskLevel}`) }}
        </Badge>
        <p class="text-xs text-n-slate-11 mt-3">
          {{ $t('CONTRACTS.FINANCIERO.RISK_NOTE') }}
        </p>
      </div>

      <div
        v-if="tieneDeuda"
        class="bg-n-background border border-n-weak rounded-lg p-4"
      >
        <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
          {{ $t('CONTRACTS.DEUDA.RECOMMENDED_ACTIONS') }}
        </h3>
        <div class="p-3 bg-n-alpha-1 rounded-lg">
          <div class="flex items-center gap-2">
            <span class="i-lucide-trending-down text-n-ruby-11" />
            <p class="text-sm text-n-slate-11">
              {{ $t('CONTRACTS.DEUDA.PAYMENT_RECOMMENDATION') }}
            </p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
